import { memo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { CreatedDateFilter } from '~/components/Filters/DateFilter/CreatedDateFilter';
import { SingleFilterContainer } from '~/components/Filters/SingleFilters/SingleFilterContainer';
import { type SingleFilterProps } from '~/components/Filters/types';
import {
  setCreatedDateFilterEndDateAction,
  setCreatedDateFilterStartDateAction,
  setCreatedDateFilterTypeAction,
} from '~/store/filters/actions';
import {
  createdDateFilterDateTypeSelector,
  createdDateFilterEndDateSelector,
  createdDateFilterStartDateSelector,
} from '~/store/filters/selectors';
import { type DateRangeType } from '~/store/filters/types';

export const SingleCreatedDateFilter = memo(({ onApplyClick }: SingleFilterProps) => {
  const dispatch = useDispatch();
  const dateType = useSelector(createdDateFilterDateTypeSelector);
  const startDate = useSelector(createdDateFilterStartDateSelector);
  const endDate = useSelector(createdDateFilterEndDateSelector);

  const onDateTypeChange = useCallback(
    (type: DateRangeType) => {
      dispatch(setCreatedDateFilterTypeAction({ type }));
    },
    [dispatch],
  );

  const onStartDateChange = useCallback(
    (date: Date | [Date, Date] | null) => {
      if (Array.isArray(date)) return;
      dispatch(setCreatedDateFilterStartDateAction({ startDate: date }));
      onApplyClick();
    },
    [dispatch, onApplyClick],
  );

  const onEndDateChange = useCallback(
    (date: Date | [Date, Date] | null) => {
      if (Array.isArray(date)) return;
      dispatch(setCreatedDateFilterEndDateAction({ endDate: date }));
      onApplyClick();
    },
    [dispatch, onApplyClick],
  );

  return (
    <SingleFilterContainer currentFilter="dateCreated" onApplyClick={onApplyClick}>
      <CreatedDateFilter
        onEndDateChange={onEndDateChange}
        onStartDateChange={onStartDateChange}
        endDate={endDate}
        startDate={startDate}
        dateType={dateType}
        onTypeChange={onDateTypeChange}
      />
    </SingleFilterContainer>
  );
});

SingleCreatedDateFilter.displayName = 'SingleCreatedDateFilter';
